import { createFileRoute, Link } from "@tanstack/react-router";
import { ClipboardList, FileClock, GitBranch, ShieldCheck, Timer, Zap } from "lucide-react";

import { seo } from "@/lib/seo";
import { getCaseStudies, getTestimonials } from "@/lib/content";
import {
  ServicePageLayout,
  serviceJsonLd,
  type ServicePageContent,
} from "@/components/service/ServicePageLayout";

const SERVICE_TAG = "workflow-automation";
const PATH = "/services/workflow-automation";
const NAME = "Workflow Automation";
const TITLE = "AI Workflow Automation for Small Business";
const SERVICE_TYPE = "AI workflow automation for small business";
const DESCRIPTION =
  "AI workflow automation for small businesses: take the repetitive operational work off your team's week without ripping out the systems you already use.";

const FAQS = [
  {
    q: "What is AI workflow automation?",
    a: "AI workflow automation uses software to move repeated work through a process, with AI added when the task needs judgment, classification, drafting, extraction, or summarising. The goal is not to make every step clever. The goal is to remove the manual handoffs, copying, waiting, and checking that keep the work from moving.",
  },
  {
    q: "What is the difference between automation and AI automation?",
    a: "Plain automation follows rules: when this happens, do that. AI-assisted automation handles work where the input is messy or the decision needs language, context, or pattern recognition. If a rule is enough, we use the rule. Adding AI where a checkbox would work makes the system harder to maintain.",
  },
  {
    q: "Which tasks should a small business automate first?",
    a: "Start with tasks that happen often, follow a mostly stable pattern, and already cause delay or errors. Good first candidates include intake routing, document handoff, appointment reminders, report assembly, follow-up drafts, and internal notifications. Avoid the rare exception process until the routine work is under control.",
  },
  {
    q: "Do we have to replace our current software?",
    a: "Almost never. Most automation connects systems you already pay for. Replacing working software is expensive and rarely the cheapest route to the same outcome. The better question is whether your current tools allow the right data to move at the right time.",
  },
  {
    q: "How do we know it is still working six months later?",
    a: "The workflow needs monitoring, an owner, and a simple way to see what ran, what failed, and what was handled by a person. We build the runbook with those checks included, so the automation is not a black box that everyone forgets until it breaks.",
  },
  {
    q: "What kinds of work should not be automated?",
    a: "Do not automate work that is rare, changing every week, legally sensitive without a review step, or dependent on facts nobody records. Also avoid automating a bad process before deciding whether the step should exist at all.",
  },
  {
    q: "Will automation put people out of work?",
    a: "That is not what we sell. Most of what we automate is work people actively dislike and never had real capacity for anyway. If headcount reduction is the goal, say that early. It changes the recommendation and may change whether we are the right firm.",
  },
  {
    q: "What happens when a system we rely on changes?",
    a: "Automations can break when a connected system changes. That is true of every integration. The practical answer is monitoring, clear failure alerts, and a documented owner who knows what to check first.",
  },
  {
    q: "What access do you need?",
    a: "Only the access required for the scoped workflow, through your accounts, with permissions you can revoke. We list what is needed before you grant it, and we avoid collecting sensitive data that the workflow does not require.",
  },
  {
    q: "Can you improve an automation we already have?",
    a: "Yes. We first look at where it fails, who trusts it, what exceptions bypass it, and whether it still matches the current process. Sometimes the fix is small. Sometimes the existing workflow should be retired rather than patched.",
  },
] as const;

const CONTENT: ServicePageContent = {
  path: PATH,
  name: NAME,
  eyebrow: "Workflow Automation",
  h1: "AI workflow automation for the work eating your team's week.",
  lead: "Data re-entry, chasing approvals, copying between systems, assembling the same report every Monday. We find the repetitive operational work, automate it against the tools you already use, and give you back the hours.",
  problem: {
    heading: "Manual work is invisible until somebody adds it up.",
    paragraphs: [
      "Nobody budgets for the time spent moving information between two systems that do not talk. It rarely appears on a P&L, so it rarely gets prioritised. It just quietly consumes the capacity you would otherwise spend on customers, staff, or the next sale.",
      "It also compounds. Manual steps are where errors enter, where things get forgotten when someone is away, and where onboarding a new hire takes longer because the real process lives in a person's head.",
      "AI workflow automation should start with the work that repeats. Some of that work needs no AI at all. A rule, form, reminder, or integration may be enough. AI belongs where the task involves messy language, documents, classification, summarising, or judgment that can be reviewed before it matters.",
    ],
  },
  useCases: {
    heading: "Where repetitive work usually shows up.",
    intro:
      "The best automation candidates are ordinary. They are the tasks everyone knows are annoying but nobody has owned long enough to fix.",
    groups: [
      {
        area: "Customer communications",
        examples: [
          "Contact forms that need to be sorted, assigned, and acknowledged before anyone follows up.",
          "Appointment reminders, confirmations, and rescheduling messages sent by hand.",
          "Customer questions that need a draft reply from approved language before a person sends it.",
          "Service updates copied from an internal system into an email or message thread.",
        ],
      },
      {
        area: "Documents and data",
        examples: [
          "Intake forms copied into three systems before work can begin.",
          "Attachments renamed, filed, and tagged according to rules nobody has documented.",
          "PDFs reviewed for missing fields before the next person can act.",
          "Exported spreadsheets cleaned before they are useful to anyone.",
        ],
      },
      {
        area: "Sales and proposals",
        examples: [
          "Lead details moved from forms into the sales record and follow-up queue.",
          "Proposal sections assembled from approved language and current scope notes.",
          "Status reminders sent when a quote or decision has gone quiet.",
          "Handoff notes created after a sale so operations knows what was promised.",
        ],
      },
      {
        area: "Scheduling",
        examples: [
          "Appointment requests checked against staff availability and business rules before confirmation.",
          "Rescheduling messages that collect constraints instead of starting another email chain.",
          "Calendar changes that notify the right people without a manager forwarding updates.",
          "Daily job lists assembled from calendars, forms, and open tasks.",
        ],
      },
      {
        area: "Knowledge",
        examples: [
          "Repeated internal questions answered from maintained policies and procedures.",
          "Training reminders triggered when a task changes or a new hire joins.",
          "Decision checklists surfaced at the point where the team needs them.",
          "Old notes turned into task-specific guidance a person can review.",
        ],
      },
      {
        area: "Finance and admin",
        examples: [
          "Invoices routed for review with missing details flagged before approval.",
          "Receipts and bills prepared for entry into your accounting system.",
          "Monthly reporting assembled from exports and checked for missing inputs.",
          "Administrative approvals tracked so stalled items have a named owner.",
        ],
      },
    ],
  },
  cards: [
    {
      icon: ClipboardList,
      title: "A process inventory",
      body: "Every repetitive task we find, with an estimate of the time it currently consumes.",
    },
    {
      icon: Zap,
      title: "Automations built and running",
      body: "Working against your live systems, not a sandbox.",
    },
    {
      icon: GitBranch,
      title: "Exception routing",
      body: "The awkward cases go to a person, with the context they need, instead of failing silently.",
    },
    {
      icon: ShieldCheck,
      title: "Error handling",
      body: "Designed for what happens when an upstream system is down or returns something unexpected.",
    },
    {
      icon: FileClock,
      title: "An audit trail",
      body: "A record of what ran, when, and what it changed.",
    },
    {
      icon: Timer,
      title: "Measured time saved",
      body: "A before-and-after number, so the investment is defensible.",
    },
  ],
  deliverables: {
    heading: "What you actually receive.",
    intro: "Built in your accounts, owned by you.",
    items: [
      "The automations, live and running in your environment",
      "The process inventory with current and projected time cost",
      "Exception handling with a named owner for each route",
      "A runbook covering what to do when one fails",
      "A handover session with the people who will own it",
    ],
  },
  readiness: {
    heading: "When automation is worth doing.",
    ready: [
      "The same task happens the same way many times a week.",
      "People can explain the normal path and the common exceptions.",
      "The task uses information that already exists in a system, document, form, or inbox.",
      "There is a clear owner for what happens when the workflow flags an exception.",
      "The team wants less copying, chasing, checking, or reporting by hand.",
    ],
    notYet: [
      "Every case is genuinely different and depends on fresh judgment from a senior person.",
      "The process is not written down anywhere and two employees describe it differently.",
      "The underlying software is being replaced soon, so the workflow target will move.",
      "The task is low-volume and takes less effort than maintaining an automation would.",
      "No one will own the workflow after launch.",
    ],
  },
  process: {
    heading: "How it runs.",
    steps: [
      {
        n: "1",
        title: "Discovery",
        body: "We find out whether there is enough repetitive work here to be worth automating.",
      },
      {
        n: "2",
        title: "Process inventory",
        body: "We shadow the work and write down what is actually happening, including the workarounds.",
      },
      {
        n: "3",
        title: "Prioritise",
        body: "We rank by value against effort to build, and start with the clearest win.",
      },
      {
        n: "4",
        title: "Build and test",
        body: "Against real cases, including the ones that usually break things.",
      },
      {
        n: "5",
        title: "Deploy and hand over",
        body: "Into production, monitored, documented, with your team trained to own it.",
      },
    ],
  },
  timeline: {
    heading: "How long it takes.",
    body: "The first useful automation is often measured in days or a few weeks once the process is understood. Workflows that touch several systems, sensitive records, or customer-facing messages need more review and testing. We keep the timeline broad until we know the systems, exception paths, and approval rules.",
  },
  pricing: {
    heading: "What it costs.",
    body: "Automation work is fixed-quote against a written scope. Where the value is measurable, we show you the payback estimate before you commit.",
  },
  related: {
    heading: "Where automation sits in the rest of the work.",
    body: (
      <>
        <p>
          Not sure this is the right starting point? The{" "}
          <Link to="/tools/ai-opportunity-scorecard">free AI readiness scorecard</Link> takes a few
          minutes and will tell you whether the repetitive work is where your best opportunity
          actually is.
        </p>
        <p>
          Some of what looks like a routing problem needs a model in the middle of it, which is{" "}
          <Link to="/services/ai-implementation">AI implementation</Link>. And once a workflow runs
          itself, the people around it still need{" "}
          <Link to="/services/ai-enablement">AI training and adoption</Link> to trust it.
        </p>
      </>
    ),
  },
  faqs: FAQS,
  cta: {
    heading: "What is your team doing twice?",
    line: "A short call is usually enough to spot the first candidate.",
  },
};

export const Route = createFileRoute("/_site/services/workflow-automation")({
  head: () =>
    seo({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      jsonLd: serviceJsonLd({
        path: PATH,
        name: NAME,
        description: DESCRIPTION,
        serviceType: SERVICE_TYPE,
        faqs: FAQS,
      }),
    }),
  loader: async () => {
    const [caseStudies, testimonials] = await Promise.all([
      getCaseStudies({ data: {} }),
      getTestimonials({ data: { limit: 3 } }),
    ]);
    return {
      caseStudies: caseStudies.filter((study) => study.services?.includes(SERVICE_TAG)).slice(0, 3),
      testimonials: testimonials.filter((t) => !t.service_tag || t.service_tag === SERVICE_TAG),
    };
  },
  component: Page,
  errorComponent: () => <ServicePageLayout content={CONTENT} />,
});

function Page() {
  const { caseStudies, testimonials } = Route.useLoaderData();
  return (
    <ServicePageLayout content={CONTENT} caseStudies={caseStudies} testimonials={testimonials} />
  );
}
